import { useRef, useState } from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { buildValidationSchema, setInitialValues } from '@comicrelief/component-library';

import { useAppSelector } from '@/store/hooks';
import { MarketingPreferencesUpdateResponse } from '@/api/payments/updateMarketingPreferences';
import { MarketingPreferencesData } from './types';
import { useUpdateMarketingPreferences } from './useUpdateMarketingPreferences';
import Loader from '@/components/loader/Loader';
import SoftHider from '@/components/softHider/SoftHider';

import { StyledMarketingPreferencesDS } from './MarketingPreferences.style';
import { SubmitButton } from '../global/Global.style';
import InformationSection from './InformationSection';

import CopyBottom from './CopyBottom';
import CopyTop from './CopyTop';

function MarketingPreferences(callbacks?: {
  onSuccess?: (data: MarketingPreferencesUpdateResponse) => void,
  onError?: (error?: unknown) => void,
}) {
  const onSuccess = useRef<((data: MarketingPreferencesUpdateResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);
  onSuccess.current = callbacks?.onSuccess;
  onError.current = callbacks?.onError;

  const [processing, setProcessing] = useState(false);
  const userEmail = useAppSelector(state => state.userDetails.email);

  const validationOverrides = {
    // Disable the postal entry as per https://comicrelief.atlassian.net/browse/ENG-3466
    mp_permissionPost: { disableOption: true },
    // As our main email field is required (and our data is only set up as one email
    // address per user), we can hide this field, and just populate it behind the scenes.
    // However, some PSPs circumnavigate userDetail entry steps, so check before assuming so:
    mp_permissionEmail: { hideInput: Boolean(userEmail) },
  };

  // Pass in our previously-supplied email address to the MP equivalent field,
  // default state is an empty string, so this still okay even if it's not yet been set,
  // as per the note above:
  const initialValueOverrides = {
    mp_email: userEmail
  };

  const {
    mpValidationSchema,
    mpValidationOptions,
  } = buildValidationSchema(validationOverrides);

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(mpValidationSchema),
    defaultValues: setInitialValues(initialValueOverrides),
  });

  const {
    handleSubmit
  } = formMethods;

  const { triggerUpdateApi } = useUpdateMarketingPreferences();

  const onSubmit = async (formData: unknown) => {
    // Cast data as MarketingPreferencesData (typescript import from component-library is mixed up!)
    const formPrefsData = formData as MarketingPreferencesData;

    setProcessing(true);

    void triggerUpdateApi(
      formPrefsData,
      {
        onSuccess: (data: MarketingPreferencesUpdateResponse) => {
          setProcessing(false);

          if (onSuccess.current) {
            onSuccess.current(data);
          }
        },
        onError: () => {
          setProcessing(false);

          if (onError.current) {
            onError.current();
          }
        },
      },
    );
  };

  return (
    <SoftHider show={!processing} loader={<Loader />}>
      <FormProvider {...formMethods}>
        <form onSubmit={event => void handleSubmit(onSubmit)(event)} noValidate>

          <StyledMarketingPreferencesDS
            formContext={formMethods}
            mpValidationOptions={mpValidationOptions}
            copyTop={CopyTop}
            copyBottom={CopyBottom}
          />

          <InformationSection />

          <SubmitButton
            type="submit"
            fullWidth
            data-test="marketingPreferencesContinue"
          >
            Save and continue
          </SubmitButton>

        </form>
      </FormProvider>
    </SoftHider>
  );
}

export default MarketingPreferences;
